import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useScrollToElement } from 'typescript/hooks/use_scroll_to_element';
import { useAddOns } from 'typescript/pages/product/hooks/use_add_ons';
import { useUpdateItemQuantity } from 'typescript/pages/product/hooks/use_update_item_quantity';

// TODO: we'll eventually move these to be typescript files
import { STICKY_HEADER_OFFSET } from 'components/pages/product/helpers';
import { usePromotionEligibility } from 'components/pages/product/hooks/use_promotion_eligibility';

// TODO: we'll eventually need to define a proper type for these or just swap to using RPC defined proto typings
type Product = Record<string, any>;
type Cart = Record<string, any>;
type Experiments = Record<string, any>;
type AddOns = Record<string, any>;

interface Selection {
  quantity: number;
  addOns: Record<string, string>;
}

interface ProductContextProps {
  children: ReactNode;
  product: Product;
  cart: Cart;
  experiments: Experiments;
}

interface ProductContextType {
  product: Product;
  currentProduct: Product | null;
  setCurrentProduct: React.Dispatch<React.SetStateAction<Product | null>>;
  selection: Selection;
  setSelection: React.Dispatch<React.SetStateAction<Selection>>;
  addOns: AddOns;
  isGetAddOnsLoading: boolean;
  isGetAddOnsError: boolean;
  cart: Cart | null;
  experiments: Experiments;
  onUpdateItemQuantity: ReturnType<
    typeof useUpdateItemQuantity
  >['onUpdateItemQuantity'];
  isUpdateItemQuantityLoading: boolean;
  scrollToRef: React.RefObject<HTMLDivElement>;
  scrollToElement: () => void;
  mainProductAdded: boolean;
  promoEligibility: Record<string, any>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProductContext = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error(
      'useProductContext must be used within a ProductContextProvider',
    );
  }
  return context;
};

const ProductContextProvider = ({
  children,
  product,
  cart: initialCart,
  experiments,
}: ProductContextProps) => {
  // current product is the product that is currently being viewed
  // this could be the single product on the page or the selected alternative
  const [currentProduct, setCurrentProduct] = useState<Product | null>(product);

  // selection is the quantity and add ons that are currently selected
  // ideally we eventually use react-hook-form to manage this but for now we'll just use a local state
  const [selection, setSelection] = useState<Selection>({
    quantity: 1,
    addOns: {},
  });

  // initial cart is the one that is passed in from our ruby backend
  // as items get added to cart, we'll update the cart state and use that instead of
  // tracking things like item count and subtotal using additional state
  // ideally in the future, we can also use react-query to manage the cart state
  const [cart, setCart] = useState<Cart | null>(initialCart);

  // tracks whether the main product has been added to cart (for showing upsell accordion)
  const [mainProductAdded, setMainProductAdded] = useState(false);

  // reset mainProductAdded when currentProduct changes (alternative selection)
  useEffect(() => {
    setMainProductAdded(false);
  }, [currentProduct?.slug]);

  // get addons, will auto update when currentProduct changes
  const { addOns, isGetAddOnsLoading, isGetAddOnsError } = useAddOns({
    product,
    currentProduct,
    cart,
    experiments,
  });

  // get promotion eligibility, will auto update when cart or currentProduct changes
  const { promoEligibility } = usePromotionEligibility(cart, currentProduct);

  // for the upsell and subtotal boxes, we'll scroll to the element when the main product is added to cart
  const { elementRef, scrollToElement } =
    useScrollToElement(STICKY_HEADER_OFFSET);

  const handleMainProductAdded = useCallback(() => {
    setMainProductAdded(true);
    scrollToElement();
  }, [scrollToElement]);

  const { onUpdateItemQuantity, isUpdateItemQuantityLoading } =
    useUpdateItemQuantity({
      currentProduct,
      cart,
      addOns,
      setCart,
      onMainProductAdded: handleMainProductAdded,
    });

  const contextValue = useMemo(
    () => ({
      product,
      currentProduct,
      setCurrentProduct,
      selection,
      setSelection,
      addOns,
      isGetAddOnsLoading,
      isGetAddOnsError,
      cart,
      experiments,
      onUpdateItemQuantity,
      isUpdateItemQuantityLoading,
      scrollToRef: elementRef,
      scrollToElement,
      mainProductAdded,
      promoEligibility,
    }),
    [
      product,
      currentProduct,
      selection,
      addOns,
      isGetAddOnsLoading,
      isGetAddOnsError,
      cart,
      experiments,
      onUpdateItemQuantity,
      isUpdateItemQuantityLoading,
      scrollToElement,
      mainProductAdded,
      promoEligibility,
    ],
  );

  return (
    <ProductContext.Provider value={contextValue}>
      {children}
    </ProductContext.Provider>
  );
};

export { ProductContextProvider };
